import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBatches, getProducts, createBatch, updateBatch, deleteBatch, getBatchTraceability, updateBatchQCStatus, getCustomers, getMakeDays } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { canEdit, getTodayFormatted } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DatePicker } from '../components/ui/date-picker';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Loader2, 
  Layers, 
  Scissors, 
  GitBranch, 
  Eye,
  Package,
  Calendar,
  ChevronRight,
  ArrowLeft,
  Box,
  ShieldCheck,
  ShieldX,
  ShieldAlert,
  Clock,
  Filter,
  X as XIcon,
  Download,
  FileText,
  Printer
} from 'lucide-react';
import { format, parse, startOfMonth, endOfMonth, eachMonthOfInterval, subMonths } from 'date-fns';

const emptyBatch = {
  product_id: '',
  batch_code: '',
  production_date: getTodayFormatted(),
  use_by_date: '',
  quantity_produced: '',
  qc_status: 'on_hold',
  make_day_id: '',
  make_day_ref: ''
};

export default function Batches() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [makeDays, setMakeDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [formData, setFormData] = useState(emptyBatch);
  const [saving, setSaving] = useState(false);
  
  // Advanced filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterPoNumber, setFilterPoNumber] = useState('');
  const [filterUseByDate, setFilterUseByDate] = useState('');
  
  // View mode: 'overview' | 'by-product' | 'by-date' | 'all'
  const [viewMode, setViewMode] = useState('overview');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  
  // Traceability dialog
  const [traceDialogOpen, setTraceDialogOpen] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);

  // QC Status dialog
  const [qcDialogOpen, setQcDialogOpen] = useState(false);
  const [qcBatch, setQcBatch] = useState(null);
  const [qcStatus, setQcStatus] = useState('');
  const [qcNotes, setQcNotes] = useState('');
  const [qcSaving, setQcSaving] = useState(false);
  
  // Product filter
  const [filterProductId, setFilterProductId] = useState('');
  const [filterQcStatus, setFilterQcStatus] = useState('');
  
  // Pagination
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  // Export functions
  const exportToCSV = () => {
    const dataToExport = filteredBatches;
    if (dataToExport.length === 0) {
      toast.error('No batches to export');
      return;
    }
    
    const headers = ['Batch Code', 'Product', 'SKU', 'Production Date', 'Use By Date', 'Qty Produced', 'Qty Available', 'Qty Dispatched', 'Weight (kg)', 'Cases', 'QC Status'];
    const rows = dataToExport.map(b => [
      b.batch_code,
      b.product_name || '',
      products.find(p => p.id === b.product_id)?.sku || '',
      b.production_date,
      b.use_by_date === 'No Date' ? 'No Date Entered' : b.use_by_date,
      b.quantity_produced?.toFixed(2) || '0.00',
      b.quantity_available?.toFixed(2) || '0.00',
      b.quantity_dispatched?.toFixed(2) || '0.00',
      b.weight_kg?.toFixed(2) || '',
      b.cases?.toFixed(1) || '',
      b.qc_status || 'on_hold'
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batches_${format(new Date(), 'dd-MM-yyyy')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${dataToExport.length} batches to CSV`);
  };
  
  const printBatches = () => {
    const dataToExport = filteredBatches;
    if (dataToExport.length === 0) {
      toast.error('No batches to print');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Batch Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
          h1 { font-size: 18px; margin-bottom: 5px; }
          .subtitle { color: #666; margin-bottom: 15px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
          th { background: #f5f5f5; font-weight: 600; }
          .mono { font-family: monospace; }
          .right { text-align: right; }
          .center { text-align: center; }
          .status-released { background: #d1fae5; color: #047857; }
          .status-on_hold { background: #fef3c7; color: #d97706; }
          .status-rejected { background: #fee2e2; color: #dc2626; }
          @media print { @page { size: landscape; margin: 10mm; } }
        </style>
      </head>
      <body>
        <h1>Batch Report</h1>
        <div class="subtitle">Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')} | ${dataToExport.length} batches</div>
        <table>
          <thead>
            <tr>
              <th>Batch Code</th>
              <th>Product</th>
              <th>Production</th>
              <th>Use By</th>
              <th class="right">Produced</th>
              <th class="right">Available</th>
              <th class="right">Dispatched</th>
              <th class="right">Weight</th>
              <th class="right">Cases</th>
              <th class="center">QC</th>
            </tr>
          </thead>
          <tbody>
            ${dataToExport.map(b => `
              <tr>
                <td class="mono">${b.batch_code}</td>
                <td>${b.product_name || ''}</td>
                <td class="mono">${b.production_date}</td>
                <td class="mono">${b.use_by_date === 'No Date' ? 'No Date Entered' : b.use_by_date}</td>
                <td class="right mono">${b.quantity_produced?.toFixed(2) || '0.00'}</td>
                <td class="right mono">${b.quantity_available?.toFixed(2) || '0.00'}</td>
                <td class="right mono">${b.quantity_dispatched?.toFixed(2) || '0.00'}</td>
                <td class="right mono">${b.weight_kg?.toFixed(2) || '-'}</td>
                <td class="right mono">${b.cases?.toFixed(1) || '-'}</td>
                <td class="center status-${b.qc_status || 'on_hold'}">${b.qc_status === 'released' ? 'Released' : b.qc_status === 'rejected' ? 'Rejected' : 'On Hold'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  useEffect(() => {
    loadData();
  }, []);

  // Reload batches when filters change
  useEffect(() => {
    loadBatches();
  }, [filterCustomerId, filterPoNumber, filterUseByDate]);

  const loadData = async () => {
    try {
      const [batchesRes, productsRes, customersRes, mdRes] = await Promise.allSettled([
        getBatches(),
        getProducts({ is_active: true }),
        getCustomers(),
        getMakeDays(),
      ]);
      if (batchesRes.status === 'fulfilled') setBatches(batchesRes.value.data || []);
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data || []);
      if (customersRes.status === 'fulfilled') setCustomers(customersRes.value.data || []);
      if (mdRes.status === 'fulfilled') setMakeDays(mdRes.value.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadBatches = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCustomerId) params.customer_id = filterCustomerId;
      if (filterPoNumber) params.po_number = filterPoNumber;
      if (filterUseByDate) params.use_by_date = filterUseByDate;
      
      const batchesRes = await getBatches(params);
      setBatches(batchesRes.data);
    } catch (error) {
      console.error('Failed to load batches', error);
    }
  };

  const hasActiveFilters = filterCustomerId || filterPoNumber || filterUseByDate || filterProductId || filterQcStatus;

  const clearFilters = () => {
    setFilterCustomerId('');
    setFilterPoNumber('');
    setFilterUseByDate('');
    setFilterProductId('');
    setFilterQcStatus('');
    setSearch('');
  };

  // Group batches by product
  const batchesByProduct = useMemo(() => {
    const grouped = {};
    batches.forEach(batch => {
      const productId = batch.product_id;
      if (!grouped[productId]) {
        grouped[productId] = {
          product: products.find(p => p.id === productId) || { name: 'Unknown Product' },
          batches: [],
          totalAvailable: 0
        };
      }
      grouped[productId].batches.push(batch);
      grouped[productId].totalAvailable += batch.quantity_available || 0;
    });
    return grouped;
  }, [batches, products]);

  // Group batches by production month
  const batchesByMonth = useMemo(() => {
    const grouped = {};
    batches.forEach(batch => {
      try {
        const prodDate = parse(batch.production_date, 'dd/MM/yyyy', new Date());
        const monthKey = format(prodDate, 'yyyy-MM');
        const monthLabel = format(prodDate, 'MMMM yyyy');
        if (!grouped[monthKey]) {
          grouped[monthKey] = {
            label: monthLabel,
            batches: [],
            totalAvailable: 0
          };
        }
        grouped[monthKey].batches.push(batch);
        grouped[monthKey].totalAvailable += batch.quantity_available || 0;
      } catch {
        // Skip batches with invalid dates
      }
    });
    // Sort by date descending
    return Object.entries(grouped)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});
  }, [batches]);

  // Filter batches based on view
  const filteredBatches = useMemo(() => {
    let result = batches;
    
    if (selectedProduct) {
      result = result.filter(b => b.product_id === selectedProduct);
    }
    
    if (selectedMonth) {
      result = result.filter(b => {
        try {
          const prodDate = parse(b.production_date, 'dd/MM/yyyy', new Date());
          return format(prodDate, 'yyyy-MM') === selectedMonth;
        } catch {
          return false;
        }
      });
    }
    
    // Product filter
    if (filterProductId) {
      result = result.filter(b => b.product_id === filterProductId);
    }
    
    // QC Status filter
    if (filterQcStatus) {
      result = result.filter(b => (b.qc_status || 'on_hold') === filterQcStatus);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(b => 
        b.batch_code?.toLowerCase().includes(searchLower) ||
        b.product_name?.toLowerCase().includes(searchLower) ||
        b.production_date?.includes(searchLower) ||
        b.use_by_date?.includes(searchLower)
      );
    }
    
    return result;
  }, [batches, selectedProduct, selectedMonth, search, filterProductId, filterQcStatus]);

  // Paginated batches
  const paginatedBatches = useMemo(() => {
    if (pageSize === 'all') return filteredBatches;
    const start = (currentPage - 1) * pageSize;
    return filteredBatches.slice(start, start + pageSize);
  }, [filteredBatches, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    if (pageSize === 'all') return 1;
    return Math.ceil(filteredBatches.length / pageSize);
  }, [filteredBatches.length, pageSize]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterProductId, filterQcStatus, selectedProduct, selectedMonth, pageSize]);

  const openCreateDialog = () => {
    setEditingBatch(null);
    setFormData({
      ...emptyBatch,
      product_id: selectedProduct || ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (batch) => {
    setEditingBatch(batch);
    setFormData({
      product_id: batch.product_id || '',
      batch_code: batch.batch_code || '',
      production_date: batch.production_date || '',
      use_by_date: batch.use_by_date || '',
      quantity_produced: batch.quantity_produced?.toString() || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.product_id || !formData.batch_code || !formData.quantity_produced) {
      toast.error('Product, batch code, and quantity are required');
      return;
    }

    // Phase 2 soft warning: nudge but don't block when Make Day isn't linked
    if (!formData.make_day_id && !formData.make_day_ref) {
      toast.warning('Heads up, this batch has no linked Make Day. Backward trace from this batch will stop at the batch itself. You can link a Make Day later in /production/make-days.', { duration: 6000 });
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        quantity_produced: parseFloat(formData.quantity_produced),
      };

      if (editingBatch) {
        await updateBatch(editingBatch.id, data);
        toast.success('Batch updated');
      } else {
        await createBatch(data);
        toast.success('Batch created');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save batch');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (batch) => {
    if (!confirm(`Deactivate batch ${batch.batch_code}?`)) return;

    try {
      await deleteBatch(batch.id);
      toast.success('Batch deactivated');
      loadData();
    } catch (error) {
      toast.error('Failed to delete batch');
    }
  };

  const viewTraceability = async (batchId) => {
    setTraceLoading(true);
    setTraceDialogOpen(true);
    try {
      const response = await getBatchTraceability(batchId);
      setTraceData(response.data);
    } catch (error) {
      toast.error('Failed to load traceability data');
      setTraceDialogOpen(false);
    } finally {
      setTraceLoading(false);
    }
  };

  const goToTransform = (batch) => {
    navigate('/transformations', { state: { sourceBatch: batch } });
  };

  const openQCDialog = (batch) => {
    setQcBatch(batch);
    setQcStatus(batch.qc_status || 'on_hold');
    setQcNotes(batch.qc_notes || '');
    setQcDialogOpen(true);
  };

  const handleQCUpdate = async () => {
    if (!qcBatch) return;
    
    setQcSaving(true);
    try {
      await updateBatchQCStatus(qcBatch.id, { status: qcStatus, notes: qcNotes });
      toast.success(`Batch ${qcBatch.batch_code} QC status updated to ${qcStatus}`);
      setQcDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update QC status');
    } finally {
      setQcSaving(false);
    }
  };

  const selectProductView = (productId) => {
    setSelectedProduct(productId);
    setSelectedMonth(null);
    setViewMode('by-product');
  };

  const selectMonthView = (monthKey) => {
    setSelectedMonth(monthKey);
    setSelectedProduct(null);
    setViewMode('by-date');
  };

  const goBack = () => {
    setSelectedProduct(null);
    setSelectedMonth(null);
    setViewMode('overview');
    setSearch('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="batches-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3">
          {viewMode !== 'overview' && (
            <Button variant="ghost" size="sm" onClick={goBack}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          )}
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Batches
            </h1>
            <p className="text-sm text-muted-foreground">
              {viewMode === 'overview' && 'Browse batches by product or production date'}
              {viewMode === 'by-product' && selectedProduct && (
                <>Batches for: <span className="font-medium">{batchesByProduct[selectedProduct]?.product?.name}</span></>
              )}
              {viewMode === 'by-date' && selectedMonth && (
                <>Batches from: <span className="font-medium">{batchesByMonth[selectedMonth]?.label}</span></>
              )}
              {viewMode === 'all' && 'All batches'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Export buttons - show in list views */}
          {(viewMode === 'by-product' || viewMode === 'by-date' || viewMode === 'all') && filteredBatches.length > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={exportToCSV} title="Export to CSV">
                <Download className="w-4 h-4 mr-1" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={printBatches} title="Print / PDF">
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
            </>
          )}
          {canEdit(user?.role) && (
            <Button onClick={openCreateDialog} data-testid="btn-add-batch">
              <Plus className="w-4 h-4 mr-2" />
              Add Batch
            </Button>
          )}
        </div>
      </div>

      {/* Overview Mode - Product or Date Selection */}
      {viewMode === 'overview' && (
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-primary">{batches.length}</div>
                <p className="text-sm text-muted-foreground">Total Batches</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-emerald-600">{Object.keys(batchesByProduct).length}</div>
                <p className="text-sm text-muted-foreground">Products</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {batches.reduce((sum, b) => sum + (b.quantity_available || 0), 0).toFixed(0)}
                </div>
                <p className="text-sm text-muted-foreground">Total Available</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setViewMode('all')}>
              <CardContent className="p-4 text-center">
                <div className="text-sm font-medium text-primary">View All →</div>
                <p className="text-sm text-muted-foreground">Show all batches</p>
              </CardContent>
            </Card>
          </div>

          {/* Browse Options */}
          <Tabs defaultValue="by-product" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="by-product" className="gap-2">
                <Package className="w-4 h-4" />
                By Product
              </TabsTrigger>
              <TabsTrigger value="by-date" className="gap-2">
                <Calendar className="w-4 h-4" />
                By Production Date
              </TabsTrigger>
            </TabsList>

            <TabsContent value="by-product" className="mt-4">
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {Object.entries(batchesByProduct).map(([productId, data]) => (
                  <Card 
                    key={productId} 
                    className="cursor-pointer hover:shadow-md hover:border-primary/50 transition-all"
                    onClick={() => selectProductView(productId)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Box className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-medium">{data.product?.name}</h3>
                            <p className="text-sm text-muted-foreground">
                              {data.batches.length} batch{data.batches.length !== 1 ? 'es' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-emerald-600">{data.totalAvailable.toFixed(0)}</div>
                          <div className="text-xs text-muted-foreground">available</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {Object.keys(batchesByProduct).length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    No batches found. Create your first batch to get started.
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="by-date" className="mt-4">
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {Object.entries(batchesByMonth).map(([monthKey, data]) => (
                  <Card 
                    key={monthKey} 
                    className="cursor-pointer hover:shadow-md hover:border-primary/50 transition-all"
                    onClick={() => selectMonthView(monthKey)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-blue-500" />
                          </div>
                          <div>
                            <h3 className="font-medium">{data.label}</h3>
                            <p className="text-sm text-muted-foreground">
                              {data.batches.length} batch{data.batches.length !== 1 ? 'es' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-blue-600">{data.totalAvailable.toFixed(0)}</div>
                          <div className="text-xs text-muted-foreground">available</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {Object.keys(batchesByMonth).length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    No batches found. Create your first batch to get started.
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Batch List View */}
      {(viewMode === 'by-product' || viewMode === 'by-date' || viewMode === 'all') && (
        <div className="space-y-4">
          {/* Search and Filters */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search batches by code or product..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="search-batches"
                  />
                </div>
                <Button 
                  variant={showFilters ? "secondary" : "outline"} 
                  onClick={() => setShowFilters(!showFilters)}
                  className="shrink-0"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                  {hasActiveFilters && (
                    <span className="ml-2 bg-primary text-primary-foreground rounded-full w-5 h-5 text-xs flex items-center justify-center">
                      {[filterCustomerId, filterPoNumber, filterUseByDate, filterProductId, filterQcStatus].filter(Boolean).length}
                    </span>
                  )}
                </Button>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <XIcon className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              
              {/* Advanced Filters */}
              {showFilters && (
                <div className="border-t pt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Filter by Product</Label>
                    <SearchableSelect
                      options={products.map(p => ({ value: p.id, label: p.name }))}
                      value={filterProductId}
                      onValueChange={setFilterProductId}
                      placeholder="All products..."
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Filter by QC Status</Label>
                    <Select value={filterQcStatus || 'all'} onValueChange={(val) => setFilterQcStatus(val === 'all' ? '' : val)}>
                      <SelectTrigger>
                        <SelectValue placeholder="All statuses..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="on_hold">On Hold</SelectItem>
                        <SelectItem value="released">Released</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Filter by Customer</Label>
                    <SearchableSelect
                      options={customers.map(c => ({ value: c.id, label: c.name }))}
                      value={filterCustomerId}
                      onValueChange={setFilterCustomerId}
                      placeholder="All customers..."
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Filter by PO Number</Label>
                    <Input
                      value={filterPoNumber}
                      onChange={(e) => setFilterPoNumber(e.target.value)}
                      placeholder="Search PO..."
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Filter by Use By Date</Label>
                    <DatePicker
                      value={filterUseByDate}
                      onChange={setFilterUseByDate}
                      placeholder="Select date..."
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results count and pagination controls */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              Showing {paginatedBatches.length} of {filteredBatches.length} batch{filteredBatches.length !== 1 ? 'es' : ''}
              {pageSize !== 'all' && totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Show:</Label>
              <Select value={pageSize.toString()} onValueChange={(v) => setPageSize(v === 'all' ? 'all' : parseInt(v))}>
                <SelectTrigger className="w-24 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="150">150</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                  <SelectItem value="250">250</SelectItem>
                  <SelectItem value="300">300</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              {pageSize !== 'all' && totalPages > 1 && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronRight className="w-4 h-4 rotate-180" />
                  </Button>
                  <span className="text-sm font-medium">{currentPage}</span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Batch Table */}
          <Card>
            <CardContent className="p-0">
              {paginatedBatches.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Batch Code</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Product</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Production</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Use By</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Produced</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Available</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Dispatched</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">QC Status</th>
                        <th className="w-[140px]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {paginatedBatches.map(batch => {
                        const isLowStock = (batch.quantity_available || 0) < 5 && (batch.quantity_available || 0) > 0;
                        const isOutOfStock = (batch.quantity_available || 0) === 0;
                        const qcStatus = batch.qc_status || 'on_hold';
                        return (
                          <tr key={batch.id} className="hover:bg-muted/30">
                            <td className="px-4 py-3 font-mono font-medium">{batch.batch_code}</td>
                            <td className="px-4 py-3">{batch.product_name}</td>
                            <td className="px-4 py-3 font-mono text-sm">{batch.production_date}</td>
                            <td className={`px-4 py-3 font-mono text-sm ${batch.use_by_date === 'No Date' ? 'text-amber-600 italic' : ''}`}>
                              {batch.use_by_date === 'No Date' ? 'No Date Entered' : batch.use_by_date}
                            </td>
                            <td className="px-4 py-3 text-right font-mono">{batch.quantity_produced?.toFixed(2)}</td>
                            <td className={`px-4 py-3 text-right font-mono font-medium ${
                              isOutOfStock ? 'text-red-600' : isLowStock ? 'text-amber-600' : 'text-emerald-600'
                            }`}>
                              {batch.quantity_available?.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-muted-foreground">
                              {batch.quantity_dispatched?.toFixed(2) || '0.00'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium cursor-pointer transition-colors ${
                                  qcStatus === 'released' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' :
                                  qcStatus === 'rejected' ? 'bg-red-100 text-red-700 hover:bg-red-200' :
                                  'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                }`}
                                onClick={() => openQCDialog(batch)}
                                title="Click to update QC status"
                              >
                                {qcStatus === 'released' ? <ShieldCheck className="w-3 h-3" /> :
                                 qcStatus === 'rejected' ? <ShieldX className="w-3 h-3" /> :
                                 <Clock className="w-3 h-3" />}
                                {qcStatus === 'on_hold' ? 'On Hold' : qcStatus.charAt(0).toUpperCase() + qcStatus.slice(1)}
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-1">
                                <Button size="sm" variant="ghost" onClick={() => viewTraceability(batch.id)} title="View Traceability">
                                  <GitBranch className="w-4 h-4" />
                                </Button>
                                {canEdit(user?.role) && (
                                  <>
                                    <Button size="sm" variant="ghost" onClick={() => goToTransform(batch)} title="Transform">
                                      <Scissors className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => openEditDialog(batch)} title="Edit">
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => handleDelete(batch)} title="Delete" className="text-red-600 hover:text-red-700">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  <Layers className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No batches found</p>
                  {search && <p className="text-sm">Try adjusting your search</p>}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingBatch ? 'Edit Batch' : 'Create New Batch'}</DialogTitle>
            <DialogDescription>
              {editingBatch ? (
                <>
                  Update batch details. 
                  <span className="text-amber-600 font-medium"> Changes to batch code or use-by date will automatically update all related dispatch notes.</span>
                </>
              ) : 'Add a new production batch'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Product *</Label>
              <SearchableSelect
                options={products.map(p => ({ value: p.id, label: p.name }))}
                value={formData.product_id}
                onValueChange={(v) => setFormData({ ...formData, product_id: v })}
                placeholder="Select product..."
                searchPlaceholder="Search products..."
              />
            </div>
            <div className="space-y-2">
              <Label>Batch Code *</Label>
              <Input
                value={formData.batch_code}
                onChange={(e) => setFormData({ ...formData, batch_code: e.target.value })}
                placeholder="e.g., CB24010101"
                className="font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Production Date</Label>
                <DatePicker
                  value={formData.production_date || ''}
                  onChange={(formattedDate) => {
                    setFormData({ ...formData, production_date: formattedDate });
                  }}
                  placeholder="Select date"
                />
              </div>
              <div className="space-y-2">
                <Label>Use By Date</Label>
                {formData.use_by_date === 'No Date' ? (
                  <div className="h-9 flex items-center px-3 bg-amber-50 border border-amber-200 rounded-md">
                    <span className="text-sm font-medium text-amber-700">No Date Entered</span>
                  </div>
                ) : (
                  <DatePicker
                    value={formData.use_by_date || ''}
                    onChange={(formattedDate) => {
                      setFormData({ ...formData, use_by_date: formattedDate });
                    }}
                    placeholder="Select date"
                  />
                )}
                <div className="flex items-center gap-1.5 mt-1">
                  <Checkbox
                    id="batch-no-date"
                    checked={formData.use_by_date === 'No Date'}
                    onCheckedChange={(checked) => {
                      setFormData({ ...formData, use_by_date: checked ? 'No Date' : '' });
                    }}
                    className="h-3.5 w-3.5"
                    data-testid="batch-no-date-checkbox"
                  />
                  <label htmlFor="batch-no-date" className="text-xs text-muted-foreground cursor-pointer">
                    No Use By Date
                  </label>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Quantity Produced *</Label>
              <Input
                type="number"
                value={formData.quantity_produced}
                onChange={(e) => setFormData({ ...formData, quantity_produced: e.target.value })}
                placeholder="e.g., 100"
                className="font-mono"
              />
            </div>
          </div>

          {/* Phase 3: Make Day picker (recommended, not enforced) */}
          <div className="space-y-2 mt-3">
            <Label className="flex items-center gap-2">Linked Make Day
              <span className="text-xs font-normal text-amber-700">strongly recommended for traceability</span>
            </Label>
            <SearchableSelect
              value={formData.make_day_id || ''}
              onValueChange={(v) => {
                const md = makeDays.find(m => m.id === v);
                setFormData({ ...formData, make_day_id: v, make_day_ref: md?.make_day_ref || '' });
              }}
              options={makeDays.map(m => ({ value: m.id, label: `${m.make_day_ref} · ${m.make_date}${m.product_type ? ' · ' + m.product_type : ''}` }))}
              placeholder="Choose a Make Day…"
              data-testid="batch-make-day-picker"
            />
            {!formData.make_day_id && (
              <div className="text-xs text-amber-700 flex items-start gap-1.5">
                <span>⚠</span>
                <span>Without a linked Make Day, backward trace from this batch will stop at the batch itself. You can link one later.</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingBatch ? 'Save Changes' : 'Create Batch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Traceability Dialog */}
      <Dialog open={traceDialogOpen} onOpenChange={setTraceDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5" />
              Batch Traceability
            </DialogTitle>
          </DialogHeader>
          {traceLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : traceData ? (
            <div className="space-y-4">
              {/* Current Batch */}
              <Card className="border-primary">
                <CardHeader className="py-3">
                  <CardTitle className="text-base">Current Batch</CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Batch:</span> <span className="font-mono">{traceData.batch?.batch_code}</span></div>
                    <div><span className="text-muted-foreground">Product:</span> {traceData.batch?.product_name}</div>
                    <div><span className="text-muted-foreground">Production:</span> {traceData.batch?.production_date}</div>
                    <div><span className="text-muted-foreground">Use By:</span> <span className={traceData.batch?.use_by_date === 'No Date' ? 'text-amber-600 italic' : ''}>{traceData.batch?.use_by_date === 'No Date' ? 'No Date Entered' : traceData.batch?.use_by_date}</span></div>
                  </div>
                </CardContent>
              </Card>

              {/* Upstream */}
              {traceData.upstream && traceData.upstream.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">↑ Upstream (Source Batches)</h4>
                  <div className="space-y-2">
                    {traceData.upstream.map((b, i) => (
                      <Card key={i} className="bg-muted/30">
                        <CardContent className="py-2 px-3">
                          <div className="text-sm">
                            <span className="font-mono">{b.batch_code}</span> - {b.product_name}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Downstream */}
              {traceData.downstream && traceData.downstream.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">↓ Downstream (Child Batches)</h4>
                  <div className="space-y-2">
                    {traceData.downstream.map((b, i) => (
                      <Card key={i} className="bg-muted/30">
                        <CardContent className="py-2 px-3">
                          <div className="text-sm">
                            <span className="font-mono">{b.batch_code}</span> - {b.product_name}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Dispatches */}
              {traceData.dispatches && traceData.dispatches.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">📦 Dispatched To</h4>
                  <div className="space-y-2">
                    {traceData.dispatches.map((d, i) => (
                      <Card key={i} className="bg-emerald-50">
                        <CardContent className="py-2 px-3">
                          <div className="text-sm">
                            <span className="font-mono">{d.dispatch_number}</span> - {d.customer_name} ({d.dispatch_date})
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* QC Status Dialog */}
      <Dialog open={qcDialogOpen} onOpenChange={setQcDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" />
              Update QC Status
            </DialogTitle>
            <DialogDescription>
              Batch: <span className="font-mono font-medium">{qcBatch?.batch_code}</span>
              <br />
              <span className="text-xs">Only released batches can be used in dispatches.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>QC Status *</Label>
              <Select value={qcStatus} onValueChange={setQcStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="on_hold">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span>On Hold</span>
                      <span className="text-xs text-muted-foreground">- Awaiting QC review</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="released">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-600" />
                      <span>Released</span>
                      <span className="text-xs text-muted-foreground">- Approved for dispatch</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="rejected">
                    <div className="flex items-center gap-2">
                      <ShieldX className="w-4 h-4 text-red-600" />
                      <span>Rejected</span>
                      <span className="text-xs text-muted-foreground">- Failed QC, do not dispatch</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>QC Notes</Label>
              <Textarea
                value={qcNotes}
                onChange={(e) => setQcNotes(e.target.value)}
                placeholder="Add any QC notes or reason for status change..."
                rows={3}
              />
            </div>
            {qcBatch?.qc_released_by && (
              <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                Previously released by: {qcBatch.qc_released_by}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQcDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleQCUpdate} disabled={qcSaving}>
              {qcSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
